import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AdPlaceholder from './AdPlaceholder';
import { useIsMobile } from "@/hooks/use-mobile";
import { trackAdSenseAction } from '@/utils/analytics';
import { ADSENSE_PUBLISHER_ID_TEST, isProduction, RESPONSIVTE_SLOT_TEST } from '@/constants/adsense';
import { useDevicePlatform } from "@/utils/device";

export interface AdSenseWrapperProps {
  style?: React.CSSProperties;
  slot: string;
  publisherId: string;
}

const AdSenseWrapper: React.FC<AdSenseWrapperProps> = ({
  style = { display: 'block' },
  slot,
  publisherId
}) => {
  const location = useLocation();
  const insRef = useRef<HTMLModElement | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasRefreshed, setHasRefreshed] = useState(false);
  const insKey = `${slot}-${location.pathname}-${refreshKey}`;
  const adClient = publisherId;
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [adFilled, setAdFilled] = useState(false);
  const adFilledRef = useRef(adFilled);
  const isMobile = useIsMobile();
  const { platform } = useDevicePlatform();

  const impressionStartRef = useRef<number | null>(null);
  const impressionObserverRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    adFilledRef.current = adFilled;
  }, [adFilled]);

  useEffect(() => {
    console.log('[AdSenseWrapper] Ad slot', slot, 'mount/start. Refresh key:', refreshKey);
    trackAdSenseAction("adsense_ad_viewed", "success", platform, isMobile ? 'mobile' : 'desktop', `slot: ${slot}`);

    if (adFilled) return;
    setShowPlaceholder(true);
    setAdFilled(false);
    setHasRefreshed(false);
    let observer: MutationObserver | null = null;
    let interval: NodeJS.Timeout | null = null;
    let checks = 0;
    const el = insRef.current;

    function waitForInsInDom(insEl: HTMLElement, callback: () => void) {
      if (insEl.isConnected) {
        callback();
      } else {
        const poll = setInterval(() => {
          if (insEl.isConnected) {
            clearInterval(poll);
            callback();
          }
        }, 100);
      }
    }

    if (el) {
      waitForInsInDom(el, () => {
        window.requestAnimationFrame(() => {
          handleAdLoad();
        });
      });

      function checkAdFilled() {
        if (!el) return;
        let localFilled = false;
        const insElements = el.tagName === 'INS' ? [el] : Array.from(el.querySelectorAll('ins'));
        const slotStr = String(slot).trim();
        const slotInsElements = insElements.filter(
          ins => (ins.getAttribute('data-ad-slot') || '').trim() === slotStr && ins.hasAttribute('data-ad-status')
        );

        if (checks === 0 || localFilled) {
          console.log('[AdSenseWrapper] slotInsElements for slot', slot, ':', slotInsElements);
        }

        let anyFilled = false;
        let allUnfilled = true;
        slotInsElements.forEach(ins => {
          const status = ins.getAttribute('data-ad-status');
          if (status === 'filled') {
            anyFilled = true;
          }
          if (status !== 'unfilled') {
            allUnfilled = false;
          }
        });

        if (anyFilled) {
          localFilled = true;
        }

        if (localFilled) {
          setAdFilled(true);
          setShowPlaceholder(false);
          trackAdSenseAction("adsense_ad_loaded", "success", platform, isMobile ? 'mobile' : 'desktop', `slot: ${slot}`);
          if (interval) clearInterval(interval);
          if (observer) observer.disconnect();
        }

        checks++;
        if (localFilled || checks > 8) {
          if (!localFilled && !hasRefreshed) {
            setHasRefreshed(true);
            trackAdSenseAction("adsense_ad_failed", "failed", platform, isMobile ? 'mobile' : 'desktop', `slot: ${slot}`);
            setTimeout(() => setRefreshKey(k => k + 1), 500);
          }
          if (interval) clearInterval(interval);
          if (observer) observer.disconnect();
        }
      }

      observer = new MutationObserver(() => {
        setTimeout(() => {
          checkAdFilled();
        }, 400);
      });

      observer.observe(el, { attributes: true, subtree: true, attributeFilter: ['data-ad-status'] });
      interval = setInterval(checkAdFilled, 1500);
      checkAdFilled();

      // ✅ Impression duration tracking
      const impressionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            impressionStartRef.current = Date.now();
          } else if (impressionStartRef.current) {
            const duration = Date.now() - impressionStartRef.current;
            trackAdSenseAction("adsense_ad_impression", "success", platform, isMobile ? 'mobile' : 'desktop', `slot: ${slot} - duration: ${duration}ms`);
            impressionStartRef.current = null;
          }
        },
        { threshold: 0.5 }
      );
      impressionObserver.observe(el);
      impressionObserverRef.current = impressionObserver;
    }

    return () => {
      if (observer) observer.disconnect();
      if (interval) clearInterval(interval);
      if (impressionObserverRef.current) {
        impressionObserverRef.current.disconnect();
        impressionObserverRef.current = null;
      }
      console.log('[AdSenseWrapper] Ad slot', slot, 'unmount/end. Final adFilled status:', adFilledRef.current);
      setShowPlaceholder(false);
      setAdFilled(false);
      trackAdSenseAction(
        "adsense_ad_blocked",
        "failed",
        platform,
        isMobile ? 'mobile' : 'desktop',
        `slot: ${slot} unmounted`
      );
    };
  }, [slot, publisherId, location.pathname, refreshKey]);

  function handleAdLoad() {
    if (window && (window.adsbygoogle = window.adsbygoogle || [])) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        // console.error('[AdSenseWrapper] Error pushing ad:', e);
        // trackAdSenseAction("ad_failed", "failed", `slot: ${slot} - push error`);
      }
    }
  }

  return (
    <div
      style={{
        position: 'relative',
        width: style?.width || '100%',
        minHeight: style?.minHeight || (isMobile ? '300px' : '90px'),
        height: style?.height === 'auto' ? undefined : (style?.height || '100%'),
        boxSizing: 'border-box',
        background: style?.background || 'transparent',
      }}
      onClick={() => {
        trackAdSenseAction("adsense_ad_clicked", "success", platform, isMobile ? 'mobile' : 'desktop', `slot: ${slot} (estimated)`);
      }}
    >
      <ins
        key={insKey}
        className="adsbygoogle"
        style={{
          ...style,
          width: '100%',
          minHeight: isMobile ? '250px' : '90px',
        }}
        data-ad-client={isProduction() ? adClient : ADSENSE_PUBLISHER_ID_TEST}
        data-ad-slot= {isProduction() ? slot : RESPONSIVTE_SLOT_TEST}
        data-ad-format={isMobile ? "rectangle" : "auto"}
        data-full-width-responsive="true"
        ref={insRef}
        data-adtest= {isProduction() ? "off" :"on" }
      />
      {showPlaceholder && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            transition: 'opacity 0.18s cubic-bezier(0.4,0,0.2,1)',
            willChange: 'opacity',
            opacity: adFilled ? 0 : 1,
            width: style?.width || '100%',
            minHeight: isMobile ? '250px' : '90px',
          }}
        >
          <AdPlaceholder style={{
            width: '100%',
            minHeight: isMobile ? '250px' : '90px',
          }} />
        </div>
      )}
    </div>
  );
};

export default AdSenseWrapper;
